package com.routexk.quiz.pages

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.analytics.FbPixel
import com.routexk.quiz.analytics.Ga4
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.FunnelStore
import com.routexk.quiz.funnel.QuizDefinition
import com.routexk.quiz.funnel.QuizStepId
import com.routexk.quiz.funnel.asMoney
import com.routexk.quiz.funnel.ui.FunnelCta
import com.routexk.quiz.funnel.ui.FunnelLayout
import com.routexk.quiz.funnel.ui.MoneyCard
import com.routexk.quiz.funnel.ui.ProfileChips
import com.routexk.quiz.funnel.ui.StatusBadge
import com.routexk.quiz.funnel.ui.StickyCtaBar
import com.routexk.quiz.funnel.ui.WhiteStatCard
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.routexk.quiz.ui.utils.isMobile
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

@Page("/report")
@Composable
fun ReportPage() {
    val ctx = rememberPageContext()
    val mobile = isMobile()
    val answers = remember { FunnelStore.answers }
    val estimate = remember { FunnelStore.estimate }

    LaunchedEffect(Unit) {
        if (!answers.quizComplete) ctx.router.navigateTo("/quiz")
        else {
            Ga4.screenView(
                "report", 6,
                "est_money_increase" to estimate.moneyIncreaseYearUsd,
                "hours_saved_per_year" to estimate.hoursSavedYear,
                "fuel_reduction_pct" to estimate.fuelSavingsPct,
            )
            FbPixel.pageView()
            FbPixel.track("ViewContent", params = arrayOf("content_name" to "report"))
        }
    }
    if (!answers.quizComplete) return

    val chips = buildList {
        answers.stopsIndex?.let {
            add(com.routexk.quiz.funnel.ui.ProfileChip("/funnel/chip-place-icon.svg", QuizDefinition.step(QuizStepId.STOPS).options!![it]))
        }
        answers.daysIndex?.let {
            add(com.routexk.quiz.funnel.ui.ProfileChip("/funnel/chip-calendar-icon.svg", QuizDefinition.step(QuizStepId.DAYS).options!![it]))
        }
        answers.fuelIndex?.let {
            add(com.routexk.quiz.funnel.ui.ProfileChip("/funnel/chip-fuelpump-icon.svg", QuizDefinition.step(QuizStepId.FUEL).options!![it]))
        }
        answers.earningsIndex?.let {
            add(com.routexk.quiz.funnel.ui.ProfileChip("/funnel/chip-dollar-icon.svg", QuizDefinition.step(QuizStepId.EARNINGS).options!![it]))
        }
    }

    FunnelLayout(
        title = com.routexk.quiz.i18n.t("RouteXK — Your Efficiency Report", "RouteXK — Tu reporte de eficiencia"),
        warm = true,
        maxContentWidth = if (mobile) 640.rpx else 1120.rpx,
        fillViewport = true,
        // Design: the warm wash sits higher, behind the cards row
        patternTop = if (mobile) 120.rpx else (-80).rpx
    ) {
        Column(
            Modifier.fillMaxWidth().gap(if (mobile) 24.rpx else 12.rpx).margin(top = 8.rpx).flexGrow(1),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                Modifier.fillMaxWidth().gap(12.rpx).margin(bottom = if (mobile) 0.rpx else 24.rpx)
            ) {
            H1(
                attrs = Modifier
                    .margin(0.rpx)
                    .fillMaxWidth()
                    .fontSize(if (mobile) 28.rpx else 36.rpx)
                    .fontWeight(FontWeight.Bold)
                    .textAlign(TextAlign.Start)
                    .fontFamily(AppTypography.SECONDARY_FONT_NAME, AppTypography.SECONDARY_FONT_FALLBACK)
                    .toAttrs()
            ) {
                Text(FunnelCopy.REPORT_TITLE)
            }

            if (!mobile) Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.gap(6.rpx).fillMaxWidth()
            ) {
                org.jetbrains.compose.web.dom.Img(
                    src = "/funnel/sparkles-icon-dark.svg",
                    attrs = { attr("aria-hidden", "true"); style { width(24.rpx); height(24.rpx) } }
                )
                SpanText(
                    FunnelCopy.REPORT_SUBTITLE,
                    Modifier
                        .fontSize(18.rpx)
                        .lineHeight(24.rpx)
                        .fontWeight(FontWeight.Medium)
                        .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                )
            }
            }

            ProfileChips(chips, compact = mobile)

            // Three stat cards in a row (Figma design). Numbers come from the
            // results-spec engine; the layout stays as the mockup.
            Row(
                Modifier.fillMaxWidth().gap(12.rpx).flexWrap(FlexWrap.Wrap),
                horizontalArrangement = Arrangement.Center
            ) {
                MoneyCard(
                    title = FunnelCopy.REPORT_MONEY_TITLE,
                    value = "+ " + estimate.moneyIncreaseYearUsd.asMoney(),
                    pill = FunnelCopy.REPORT_MONEY_PILL,
                    modifier = if (mobile) Modifier.fillMaxWidth() else Modifier.width(365.rpx).height(198.rpx)
                )
                WhiteStatCard(
                    title = FunnelCopy.REPORT_HOURS_TITLE,
                    icon = "/funnel/timer-icon.svg",
                    value = "${estimate.hoursSavedYear}",
                    unit = "hrs/year",
                    pill = "👀 ${estimate.workingDaysReturned} working days returned to you",
                    modifier = if (mobile) Modifier.fillMaxWidth() else Modifier.width(365.rpx).height(198.rpx)
                )
                WhiteStatCard(
                    title = FunnelCopy.REPORT_FUEL_TITLE,
                    icon = "/funnel/fuelpump-icon-24.svg",
                    value = "${estimate.fuelSavingsPct}%",
                    unit = "less",
                    pill = "💸 ≈ ${estimate.fuelSavingsYearUsd.asMoney()} back in your pocket annually",
                    modifier = if (mobile) Modifier.fillMaxWidth() else Modifier.width(365.rpx).height(198.rpx)
                )
            }

            // Spacer pins the claimed tag + CTA to the bottom of the viewport
            com.varabyte.kobweb.compose.foundation.layout.Box(Modifier.fillMaxWidth().flexGrow(1).minHeight(16.rpx))

            if (!mobile) Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(4.rpx)) {
                org.jetbrains.compose.web.dom.Img(
                    src = "/funnel/tag-check-icon.svg",
                    attrs = { attr("aria-hidden", "true"); style { width(12.rpx); height(12.rpx) } }
                )
                SpanText(
                    FunnelCopy.CLAIMED_BADGE.replace("%s", estimate.taxDeductionUsd.asMoney()),
                    Modifier.fontSize(14.rpx).lineHeight(16.rpx)
                        .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                )
            }

            if (mobile) {
                // Pinned to the viewport bottom so the CTA is reachable without
                // scrolling past the stat cards; the claimed pill rides on top
                StickyCtaBar(spacerHeight = 108) {
                    Column(
                        Modifier.fillMaxWidth().gap(8.rpx),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Row(
                            Modifier
                                .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.White)
                                .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE6, 0xE6, 0xF3))
                                .borderRadius(100.rpx)
                                .padding(topBottom = 6.rpx, leftRight = 14.rpx)
                                .gap(6.rpx)
                                .styleModifier { property("box-shadow", "0 4px 12px rgba(62, 63, 91, 0.12)") },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            org.jetbrains.compose.web.dom.Img(
                                src = "/funnel/tag-check-icon.svg",
                                attrs = { attr("aria-hidden", "true"); style { width(12.rpx); height(12.rpx) } }
                            )
                            SpanText(
                                FunnelCopy.CLAIMED_BADGE.replace("%s", estimate.taxDeductionUsd.asMoney()),
                                Modifier.fontSize(13.rpx).lineHeight(16.rpx)
                                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                            )
                        }
                        FunnelCta(
                            text = FunnelCopy.REPORT_CTA,
                            onClick = {
                                Ga4.event("get_my_plan_clicked")
                                ctx.router.navigateTo("/plan")
                            }
                        )
                    }
                }
            } else {
                FunnelCta(
                    text = FunnelCopy.REPORT_CTA,
                    onClick = {
                        Ga4.event("get_my_plan_clicked")
                        ctx.router.navigateTo("/plan")
                    }
                )
            }
        }
    }
}
